import React, { useState } from 'react';
import { styled } from '@mui/system';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';

import { cloneDeep } from 'lodash';
import { HistoryIcon } from 'public/icons';
import { addArea } from '@/slices/area/slices/areaSlice';
import { addHomePoints } from '@/slices/area/slices/searchFlightFormSlice';
import { useAppDispatch } from '@/utils/hook';
import type { SearchFlightResultItemProps } from './index.d';
import { ResultIcon } from '@/containers/Bus/SearchBusTab/SearchBusResultItem';
import AirportItem from './AirportItem';
import { removeVietnameseTones } from '@/utils/stringConvert';

const replaceText = (str: string, color?: string) => {
  if (!str) return '';
  str = str.replace(/<b>/g, `<b style="color:${color}">`);
  return str;
};
// 0172CB
const SearchFlightResultItem: React.FC<SearchFlightResultItemProps> = ({
  data,
  type,
  setClose,
  isRecent,
  isSearchResult,
  setIsInputEmpty,
  isInputEmptyRef,
  onMouseEnter,
  index,
  hoverIndex,
  lang,
}) => {
  const newDataClone = cloneDeep(data);
  const {
    location, country, airports, highlight,
  } = newDataClone;
  const dispatch = useAppDispatch();
  const [showMergeNotice, setShowMergeNotice] = useState(!!newDataClone?.legacy && !!newDataClone?.newAreaAfterMerged);

  const isHover = index === undefined ? false : String(index) === String(hoverIndex);

  const textHighLight = replaceText(highlight || location, isHover ? 'white' : '#0172CB');
  return (
    <Stack>
      <Stack
        sx={{
          cursor: 'pointer',
          flexDirection: 'row',
          padding: '4px 16px',
          background: isHover ? '#0073d2' : 'white',
        }}
      >
        {isRecent ? (
          <HistoryIcon color={isHover ? 'white' : '#8897A5'} />
        ) : (
          <ResultIcon
            color={isHover ? 'white' : '#8897A5'}
            type={data.type}
            name={data.location}
          />
        )}
        <Stack
          sx={{ pl: 1 }}
        >
          <Stack
            component="div"
            onClick={() => {
              setClose();
              if (setIsInputEmpty) {
                setIsInputEmpty(true);
                isInputEmptyRef.current = true;
              }
              dispatch(addArea({
                area: newDataClone,
              }));
              dispatch(addHomePoints({
                data: newDataClone,
                type,
              }));
            }}
            spacing={1}
            direction="row"
            onMouseEnter={() => (onMouseEnter ? onMouseEnter?.(index) : false)}
            onMouseOver={() => (onMouseEnter ? onMouseEnter?.(index) : false)}
            onMouseLeave={() => (onMouseEnter ? onMouseEnter?.('') : false)}
          >
            <Stack>
              <TitleLine isHover={isHover}>
                <TitleText
                  isHover={isHover}
                  dangerouslySetInnerHTML={{
                    __html: `${lang?.lang === 'en' ? removeVietnameseTones(textHighLight) : textHighLight}, ${lang?.lang === 'en' ? removeVietnameseTones(country) : country}`,
                  }}
                />
                {newDataClone?.legacy === 1 && (
                  <LegacyLabel>
                    CŨ
                  </LegacyLabel>
                )}
              </TitleLine>
              {airports.length > 1 && (
                <Typography
                  variant="caption"
                  color={isHover ? 'white' : '#343A40'}
                >
                  {airports.length}
                  {' '}
                  {lang?.SearchBar?.flightsTab?.airports || 'sân bay'}
                </Typography>
              )}
              {showMergeNotice && !!newDataClone?.legacy && !!newDataClone?.newAreaAfterMerged && (
                <NotiView
                  role="button"
                  tabIndex={0}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    try {
                      window.dispatchEvent(new CustomEvent('search.quickFill', { detail: newDataClone.newAreaAfterMerged?.full_name }));
                    } catch (err) {
                      // noop
                    }
                  }}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        window.dispatchEvent(new CustomEvent('search.quickFill', { detail: newDataClone.newAreaAfterMerged?.full_name }));
                      } catch (err) {
                        // noop
                      }
                    }
                  }}
                >
                  <Typography
                    className="noti-text"
                    sx={{ fontWeight: 400 }}
                  >
                    {`${newDataClone.location} đã sáp nhập với ${newDataClone.newAreaAfterMerged.full_name}, bạn có thể tìm tỉnh mới `}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          window.dispatchEvent(new CustomEvent('search.quickFill', { detail: newDataClone.newAreaAfterMerged?.full_name }));
                        } catch (err) {
                          // noop
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            window.dispatchEvent(new CustomEvent('search.quickFill', { detail: newDataClone.newAreaAfterMerged?.full_name }));
                          } catch (err) {
                            // noop
                          }
                        }
                      }}
                      style={{
                        color: '#0172CB',
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                    >
                      {`“${newDataClone.newAreaAfterMerged.full_name}”`}
                    </span>
                    {' để có kết quả cập nhật mới nhất.'}
                  </Typography>
                  <button
                    type="button"
                    className="noti-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMergeNotice(false);
                    }}
                    aria-label="close"
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </NotiView>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <AirportItem
        lang={lang}
        indexProvince={index}
        hoverIndex={hoverIndex}
        airports={airports}
        isHover={isHover}
        isRecent={isRecent}
        isSearchResult={isSearchResult}
        location={location}
        onHoverItem={(id: string) => onMouseEnter?.(id)}
        onClickItem={(airport: any) => {
          newDataClone.airports = [airport];
          if (setIsInputEmpty) {
            setIsInputEmpty(true);
            isInputEmptyRef.current = true;
          }
          setClose();
          dispatch(addArea({
            area: newDataClone,
          }));
          dispatch(addHomePoints({
            data: newDataClone,
            type,
          }));
        }}
      />
    </Stack>
  );
};

export const WrapperClockIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
`;

const LegacyLabel = styled('span')(() => ({
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: '12px',
  letterSpacing: '0.3px',
  textAlign: 'center',
  color: '#FFFFFF',
  padding: '3px 4px 1px 4px',
  borderRadius: '4px',
  backgroundColor: '#8897A5',
}));

const TitleLine = styled('div')<{ isHover: boolean }>(({ isHover }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  color: isHover ? 'white' : 'black',
}));

const TitleText = styled('span')<{ isHover: boolean }>(({ isHover }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: isHover ? 'white' : 'black',
}));

const NotiView = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #ECF4FD;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 8px;
  width: 100%;
  max-width: 230px;
  overflow: hidden;
  flex-wrap: wrap;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    max-width: none;
    width: 100%;
  }

  .noti-text {
    flex: 1 1 auto;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    margin: 0;
    color: #212529;
    user-select: none;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    overflow-wrap: break-word;
    hyphens: auto;
    min-width: 0;
    max-width: calc(100% - 36px);
    display: block;
  }

  .noti-close {
    line-height: 0;
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    flex-shrink: 0;
    margin-left: auto;
    border: none;
    background: none;

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      cursor: pointer;
    }
  }
`;

export default SearchFlightResultItem;
